import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type Invoice = components['schemas']['InvoiceResponse'];
export type InvoiceSummary = components['schemas']['InvoiceSummaryResponse'];
export type InvoiceList = components['schemas']['InvoiceListResponse'];
export type JobInvoiceCreated = components['schemas']['CreateJobInvoiceResponse'];
export type SaveInvoiceLine = components['schemas']['SaveLineItemRequest'];
export type InvoiceEmail = components['schemas']['InvoiceEmailResponse'];

/** The statuses M2-11 derives, in the order the list's filter offers them. */
export const INVOICE_STATUSES = ['Draft', 'Sent', 'Partial', 'Overdue', 'Paid', 'Void'] as const;

/**
 * The list's default view (M2-12, Kyle 2026-09-27): Sent, Partial or Overdue
 * with a balance above zero. Not a status — a `status` value the API accepts
 * alongside them. The API's own default (no status) is everything, which the
 * filter offers as "All statuses".
 */
export const OUTSTANDING = 'Outstanding';

/** The five A/R buckets, as the wire names them, in the order the strip shows them (M2-12 pass A). */
export const AGING_BUCKETS = [
  'Current',
  'Days1To30',
  'Days31To60',
  'Days61To90',
  'Over90',
] as const;
export type AgingBucket = (typeof AGING_BUCKETS)[number];

/** `sortBy`, as `InvoiceEndpoints.SortableColumns` spells it — case-sensitive on the server. */
export type InvoiceSortColumn = 'number' | 'age' | 'total' | 'balanceDue';

/**
 * A status the API accepts Void on: anything not already void — a draft
 * included since M2-10 (Kyle, 2026-10-02), so a mistaken one keeps its number
 * and its job can be invoiced again.
 */
export function canVoid(status: string): boolean {
  return status !== 'Void';
}

const INVOICES_KEY = ['invoices'] as const;

/** Every job query — the detail, the list, the board, a customer's and a property's job history. */
const JOBS_KEY = ['jobs'] as const;

/**
 * The API refused an invoice act and said why (409): already sent, already
 * void, still a draft. Carried verbatim, like `EstimateRefusedError`.
 */
export class InvoiceRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvoiceRefusedError';
  }
}

/**
 * A 400 the API explains, keyed by the field or parameter it is about — the
 * list's refusals by query parameter (`sentTo`, `status`, …), Send's by what
 * is missing (`Lines`, `JobId`, `TaxJurisdiction`, …).
 */
export class InvoiceInvalidError extends Error {
  constructor(public readonly errors: Record<string, string[]>) {
    super(Object.values(errors).flat()[0] ?? 'Invalid');
    this.name = 'InvoiceInvalidError';
  }
}

/**
 * A 403 (M2-10): a technician on an invoice that is not a draft on their job.
 * Carries the API's sentence when it gave one ("…only the office can open it
 * now"); empty when the refusal was a bare 403.
 */
export class InvoiceNotPermittedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvoiceNotPermittedError';
  }
}

/** A 404: a stale or mistyped link — a missing page, not a failed request. */
export class InvoiceNotFoundError extends Error {
  constructor() {
    super('No such invoice');
    this.name = 'InvoiceNotFoundError';
  }
}

/**
 * ⚠️ Read from openapi-fetch's `error`, never by re-reading the Response — its
 * body is already consumed. A 409 carries a JSON string; a 400 a
 * ValidationProblemDetails.
 */
function failure(error: unknown, response: Response, what: string): Error {
  if (response.status === 409) {
    return new InvoiceRefusedError(typeof error === 'string' ? error : `${what} refused`);
  }
  if (response.status === 400) {
    const problem = error as { errors?: Record<string, string[]> } | undefined;
    return new InvoiceInvalidError(problem?.errors ?? {});
  }
  return new Error(`${what} failed: ${response.status}`);
}

export interface InvoiceFilters {
  /** A status, {@link OUTSTANDING}, or '' for everything. */
  status: string;
  customerId: string;
  /** `YYYY-MM-DD`, the account's calendar, inclusive; '' for open-ended. */
  sentFrom: string;
  sentTo: string;
  /** A bucket, or '' for none. */
  aging: string;
  sortBy: InvoiceSortColumn;
  sortDirection: 'asc' | 'desc';
}

/**
 * The invoice list with its aging strip and total outstanding (M2-12). All of
 * it is the server's: the rows are filtered and sorted there, and the strip
 * and total follow only the customer and sent-date filters — clicking a bucket
 * narrows the rows and leaves the strip whole. Nothing here adds anything up.
 *
 * `keepPreviousData`, so changing a filter keeps the last answer on screen
 * rather than flashing the loading state; the page dims nothing and says
 * nothing, because the next answer is milliseconds away.
 */
export function useInvoices(filters: InvoiceFilters) {
  const query = {
    status: filters.status || undefined,
    customerId: filters.customerId || undefined,
    sentFrom: filters.sentFrom || undefined,
    sentTo: filters.sentTo || undefined,
    aging: filters.aging || undefined,
    sortBy: filters.sortBy,
    sortDirection: filters.sortDirection,
  };
  return useQuery({
    queryKey: [...INVOICES_KEY, 'list', query] as const,
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET('/api/v1/invoices', {
        params: { query },
      });
      if (!response.ok || !data) throw failure(error, response, 'GET /api/v1/invoices');
      return data;
    },
    placeholderData: keepPreviousData,
    // A 400 is the server refusing these filters; asking again cannot change that.
    retry: (count, error) => !(error instanceof InvoiceInvalidError) && count < 1,
  });
}

export function useInvoice(invoiceId: string | undefined) {
  return useQuery({
    enabled: invoiceId !== undefined,
    queryKey: [...INVOICES_KEY, 'detail', invoiceId] as const,
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET('/api/v1/invoices/{invoiceId}', {
        params: { path: { invoiceId: invoiceId! } },
      });
      if (response.status === 404) throw new InvoiceNotFoundError();
      if (response.status === 403) {
        const detail = (error as { detail?: unknown } | undefined)?.detail;
        throw new InvoiceNotPermittedError(typeof detail === 'string' ? detail : '');
      }
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/invoices/{id} failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

/**
 * The office's acts on an invoice: Send invoice (M2-16: emails the customer
 * their link), Mark as sent without emailing, Resend, and Void.
 *
 * ⚠️ Each invalidates EVERY invoice query, not just this one's detail. A send
 * or a void moves the invoice between statuses AND in or out of the
 * outstanding total and the aging strip — figures the cached list is showing,
 * which the app's 30-second staleTime would otherwise leave on screen after a
 * return to the list. A resend adds an email to the detail's history.
 *
 * ⚠️ And every JOB query (M2-10): a void moves an Invoiced job back to
 * Complete, and the job page names its invoice's status — both of which the
 * job's cached detail, the job list and the board are showing.
 */
export function useInvoiceActions(invoiceId: string) {
  const queryClient = useQueryClient();
  // The detail page is one of those queries, so it re-reads itself too.
  const onSuccess = () => {
    void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
    void queryClient.invalidateQueries({ queryKey: JOBS_KEY });
  };
  const path = { params: { path: { invoiceId } } };

  /**
   * M2-16: `/send` freezes the tax, issues the customer's link and EMAILS it;
   * the API refuses a customer with no address (409, its sentence names Mark
   * as sent without emailing). `dueDate` null is "due on receipt": the server
   * sets it to the account's today.
   */
  const send = useMutation({
    mutationFn: async (dueDate: string | null) => {
      const { data, error, response } = await apiClient.POST('/api/v1/invoices/{invoiceId}/send', {
        ...path,
        body: { dueDate },
      });
      if (!response.ok || !data) throw failure(error, response, 'send');
      return data;
    },
    onSuccess,
  });

  /** The same freeze and link as Send, and no email — for an invoice delivered by hand. */
  const markSent = useMutation({
    mutationFn: async (dueDate: string | null) => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/invoices/{invoiceId}/mark-sent',
        { ...path, body: { dueDate } },
      );
      if (!response.ok || !data) throw failure(error, response, 'mark sent');
      return data;
    },
    onSuccess,
  });

  /** The invoice email again, to the customer's address as it is now (M2-16). */
  const resend = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/invoices/{invoiceId}/resend',
        path,
      );
      if (!response.ok || !data) throw failure(error, response, 'resend');
      return data;
    },
    onSuccess,
  });

  const voidInvoice = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/invoices/{invoiceId}/void',
        path,
      );
      if (!response.ok || !data) throw failure(error, response, 'void');
      return data;
    },
    onSuccess,
  });

  return { send, markSent, resend, void: voidInvoice };
}

/** The URL the customer opens: this app's own origin, `/i/{token}` (M2-16, `CustomerLinkOptions.InvoiceUrl`). */
export function invoiceLinkUrl(token: string): string {
  return `${window.location.origin}/i/${token}`;
}

/**
 * The customer's invoice link (M2-16): copy — the live link, or a new one
 * after a revoke — and revoke. Both `customers.manage`. A copy the API refuses
 * (a draft, a void invoice) comes back as its sentence.
 *
 * Both invalidate the invoice queries: the detail's `hasCustomerLink` and its
 * next reminder (reminders pause without a live link) change with either.
 */
export function useInvoiceLink(invoiceId: string) {
  const queryClient = useQueryClient();
  const onSuccess = () => void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
  const path = { params: { path: { invoiceId } } };

  const copy = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/invoices/{invoiceId}/link',
        path,
      );
      if (!response.ok || !data) throw failure(error, response, 'copy link');
      return invoiceLinkUrl(data.token);
    },
    onSuccess,
  });

  const revoke = useMutation({
    mutationFn: async () => {
      const { error, response } = await apiClient.DELETE('/api/v1/invoices/{invoiceId}/link', path);
      if (!response.ok) throw failure(error, response, 'revoke link');
    },
    onSuccess,
  });

  return { copy, revoke };
}

/**
 * Opts this invoice in to or out of the account's payment reminders (M2-16).
 * Answers with the invoice, whose `nextReminderOn` follows; every invoice
 * query is invalidated so the detail re-reads it at the real staleTime.
 */
export function useInvoiceReminders(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data, error, response } = await apiClient.PUT(
        '/api/v1/invoices/{invoiceId}/reminders',
        { params: { path: { invoiceId } }, body: { enabled } },
      );
      if (!response.ok || !data) throw failure(error, response, 'reminders');
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: INVOICES_KEY }),
  });
}

/** Statuses the API hands out the customer's link in — anything sent and not void. */
export function hasLinkToCopy(status: string): boolean {
  return status !== 'Draft' && status !== 'Void';
}

/**
 * A Complete job's draft invoice (M2-10): `POST /jobs/{id}/invoice`. The
 * office or the technician on the job. A 201 is the new draft; a 200
 * (`AlreadyInvoiced`) names the draft the job already had — a double tap, or
 * the technician and the office together — and is treated as the same
 * success. A 409 is the API's sentence (not Complete, already sent).
 *
 * ⚠️ Invalidates every invoice query (the new draft joins the list) and every
 * job query (the job is Invoiced now, on its page, the job list and the
 * board), at the app's real 30-second staleTime.
 */
export function useCreateJobInvoice(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await apiClient.POST('/api/v1/jobs/{jobId}/invoice', {
        params: { path: { jobId } },
      });
      if (!response.ok || !data) throw failure(error, response, 'create invoice');
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      void queryClient.invalidateQueries({ queryKey: JOBS_KEY });
    },
  });
}

/**
 * Replaces a draft's lines (M2-10; `PUT /invoices/{id}`, M2-11). The lines
 * are the invoice's own copies, so nothing about the job changes — only the
 * invoice queries (its page, and the list) are invalidated.
 */
export function useSaveInvoiceLines(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (lines: SaveInvoiceLine[]) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/invoices/{invoiceId}', {
        params: { path: { invoiceId } },
        body: { lines },
      });
      if (!response.ok || !data) throw failure(error, response, 'save lines');
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: INVOICES_KEY }),
  });
}
